import { Injectable, Injector } from "@angular/core";
import { CtAttributesService } from "@cloud-twin-v2/cloud-twin-shared/core/attributes/ct-attributes.service";
import { CTResultsService } from "@cloud-twin-v2/cloud-twin-shared/core/results/ct-results.service";
import { CTMeasurementFiltrableItemDto } from "@cloud-twin-v2/cloud-twin-shared/filtrable-items/ct-measurement-filtrable-item";
import { CTResultFiltrableItemDto } from "@cloud-twin-v2/cloud-twin-shared/filtrable-items/ct-result-filtrable-item";
import { CtResult } from "@cloud-twin-v2/ct-map/models/ct-result";
import { ChartResultDto, MeasurementQueryDto, MeasurementSerieDto } from "@cloud-twin-v2/generated/simulation.generated.client";
import { TelemetryPointsService } from "@cloud-twin-v2/telemetry/services/telemetry-points.service";
import { FiltrableItemManagerService } from "@common-modules/shared/charts/filtrable-item-manager.service";
import { ChartSerie } from "@common-modules/shared/charts/model/chart/chart-serie";
import { IChartConfiguration } from "@common-modules/shared/charts/model/chart/ichart-configuration";
import { IPlotableSerie } from "@common-modules/shared/charts/model/series/plotable-serie";
import { FiltrableItemTypeEnum } from "@common-modules/shared/model/filtrable-items/types/filtrable-item-type-enum";
import { DimensionTypesEnum } from "@common-modules/shared/model/shared/dimension-types";
import { BaseCustomizableChartDataService } from "@common-modules/wlm-charts/core/customizable-chart/base-customizable-chart-data.service";
import { CustomizableChartMapperService } from "@common-modules/wlm-charts/core/customizable-chart/customizable-chart-mapper.service";
import { GCartesianChartSerie } from "@common-modules/wlm-charts/core/models/generic-chart-settings/g-cartesian-chart-series";
import { GenericCartesianChartSettings } from "@common-modules/wlm-charts/core/models/generic-chart-settings/generic-cartesian-chart-settings";
import { asEnumerable } from "linq-es2015";
import { Observable, combineLatest, map, of } from "rxjs";

@Injectable()
export class CTCustomizableChartDataService extends BaseCustomizableChartDataService {
  private _allDataPoints = [];

  constructor(private _injector: Injector,
    private _ctResultService: CTResultsService,
    private _filtrableItemManagerService: FiltrableItemManagerService,
        private _telemetryPointsService: TelemetryPointsService,
    private _chartSettingsMapperService: CustomizableChartMapperService) {
    super(_injector);
  }

  public getData(chartConfiguration: IChartConfiguration): Observable<any> {
    const series = chartConfiguration.seriesConfiguration;
    const serieQueries$: Observable<ChartSerie[]>[] = [];

    this.getCTResults(series, serieQueries$);
    this.getCTMeasurements(series, serieQueries$);

    return combineLatest(serieQueries$).pipe(
      map((series) => {
        return { chartConfiguration, series: asEnumerable(series).SelectMany().ToArray() };
      })
    );
  }

  public mapDataToGenericSettings(data: any): Observable<GenericCartesianChartSettings> {
    const chartTitle = this._chartSettingsMapperService.mapChartTitle(
      data.chartConfiguration.customization
    );

    const verticalAxes = data.chartConfiguration.verticalAxis.map((x) =>
      this._chartSettingsMapperService.mapVerticalAxis(x)
    );

    this._chartSettingsMapperService.setVerticalAxesOffset(verticalAxes);

    const horizontalAxes = data.chartConfiguration.horizontalAxis.map((x) =>
      this._chartSettingsMapperService.mapHorizontalAxis(x)
    );
    const series: GCartesianChartSerie[] = [];

    data.series.forEach((serie) => {
      const verticalAxisUnit = data.chartConfiguration.verticalAxis.find(
        (x) => x.id == serie.serieDefinition.serieCustomization.verticalAxisId
      ).unitTypeToDescription;
      const gCartesianSerie = this._chartSettingsMapperService.mapSerie(serie, verticalAxisUnit);

      gCartesianSerie.xAxisWLMDimensionTypeId = DimensionTypesEnum.NA;
      gCartesianSerie.yAxisWLMUnitCustomLabel = verticalAxisUnit;
      gCartesianSerie.yAxisName = verticalAxisUnit;
      gCartesianSerie.yAxisCustomDimension = verticalAxisUnit;
      series.push(gCartesianSerie);
    });

    const dataZoom = this._chartSettingsMapperService.mapDataZoom(
      horizontalAxes,
      verticalAxes,
      data.chartConfiguration.customization.showTimeBar
    );
    const legend = data.chartConfiguration.customization.showLegend
      ? this._chartSettingsMapperService.getSeriesLegend(series)
      : null;

    const settings = new GenericCartesianChartSettings({
      chartTitle: chartTitle,
      series,
      xAxes: horizontalAxes,
      yAxes: verticalAxes,
      dataZoom: dataZoom,
      legend: legend,
    });
    if (chartTitle) {
      settings.chartTitle = chartTitle;
    }

    return of(settings);
  }


  getCTMeasurements(series: IPlotableSerie[], serieQueries$: Observable<ChartSerie[]>[]) {
    const measurementSeries = series.filter((serie) => serie.serieDefinition.filtrableType.type === FiltrableItemTypeEnum.CTMeasurement);
    if (!measurementSeries.length) {
      return of([]);
    }

    const measurementSeriesQuery = this.getMeasurementSeriesQuery(measurementSeries);
    const query$ = this._telemetryPointsService.getMeasurementSerie(measurementSeriesQuery)
      .pipe(
        map((measurementSeries: MeasurementSerieDto[]) => {
          var chartSeries: ChartSerie[] = [];
          const service = this._filtrableItemManagerService.getService(FiltrableItemTypeEnum.CTMeasurement);
          measurementSeries.forEach((measurementSerie: MeasurementSerieDto) => {
            const currentSerie = series.find((serie) => (serie.serieDefinition as CTMeasurementFiltrableItemDto).filtrableType.pointId === measurementSerie.pointId);
            const currentFiltrableType = (currentSerie.serieDefinition as CTMeasurementFiltrableItemDto).filtrableType;
            const serieValues = service.getDataPoints(currentFiltrableType, measurementSerie);
            const chartSerie = new ChartSerie(currentSerie, serieValues);
            chartSeries.push(chartSerie);
          });
          return chartSeries;
        })
      );

    serieQueries$.push(query$);
  }

  private getMeasurementSeriesQuery(measurementSeries: IPlotableSerie[]) {
    const measurementSeriesQuery = [];
    measurementSeries.forEach((serie) => {
      const scenarioId = (serie.serieDefinition as CTMeasurementFiltrableItemDto).filtrableType.workspaceConnectSetting.scenarioId;
      const pointId = (serie.serieDefinition as CTMeasurementFiltrableItemDto).filtrableType.pointId;
      const startDate = serie.serieDefinition.startDate;
      const endDate = serie.serieDefinition.endDate;
      measurementSeriesQuery.push({
        scenarioId, pointId, startDate, endDate
      } as MeasurementQueryDto);
    });
    return measurementSeriesQuery;
  }

  private getResultSeriesQuery(resultSeries: IPlotableSerie[]){
    const resultSeriesQuery = [];
    resultSeries.forEach(serie =>{
      const serieDefinition  = (serie.serieDefinition as CTResultFiltrableItemDto).filtrableType;
      const scenarioId = serieDefinition.workspaceConnectSetting.scenarioId;
      const networkElementId = serieDefinition.networkElementId;
      const attributeId = serieDefinition.attributeId;
      const startDate = serie.serieDefinition.startDate;
      const endDate = serie.serieDefinition.endDate;
      resultSeriesQuery.push({
        scenarioId, networkElementId, attributeId, startDate, endDate
      } as ChartResultDto);
    });
    return resultSeriesQuery;
  }

  getCTResults(series: IPlotableSerie[], serieQueries$: Observable<ChartSerie[]>[]) {
    const resultSeries = series.filter((serie) => serie.serieDefinition.filtrableType.type === FiltrableItemTypeEnum.CTResult);
    if (!resultSeries.length) {
      return of([]);
    }

    const resultSeriesQuery = this.getResultSeriesQuery(resultSeries);
    const query$ = this._ctResultService.getChartResults(resultSeriesQuery)
      .pipe(
        map((scenariosResults: CtResult[]) => {
          var chartSeries: ChartSerie[] = [];
          const service = this._filtrableItemManagerService.getService(FiltrableItemTypeEnum.CTResult);
          scenariosResults.forEach((result: CtResult) => {
            var scenarioSeries = resultSeries.filter((serie) =>
              (serie.serieDefinition as CTResultFiltrableItemDto).filtrableType.workspaceConnectSetting.scenarioId === result.scenarioId);
            scenarioSeries.forEach((serie) => {
              const serieValues = service.getDataPoints(serie.serieDefinition.filtrableType, result);
              const chartSerie = new ChartSerie(serie, serieValues);
              chartSeries.push(chartSerie);
            });
          });
          return chartSeries;
        })
      );

    serieQueries$.push(query$);
  }
}
